import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExchangeAlt, faSpinner, faCheckCircle, faTimesCircle, faArrowLeft,
  faBox, faClock, faTag, faTruck, faBan, faSun, faMoon, faMobileAlt,
} from '@fortawesome/free-solid-svg-icons';
import { swapAPI, productsAPI } from '../services/api';
import { setStoredDarkMode } from '../utils/theme';
import { useSwap } from '../context/SwapContext';
import SwapOfferForm from './SwapOfferForm';
import '../assets/css/swapdeals.css';
import { useNotifications } from '../context/NotificationContext';

const getCurrentUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.id || user._id || null;
  } catch {
    return null;
  }
};

// Status stepper config
const STEPPER_STEPS = [
  { key: 'proposed',  label: 'Proposed'  },
  { key: 'accepted',  label: 'Accepted'  },
  { key: 'shipping',  label: 'Shipped'   },
  { key: 'delivery',  label: 'Delivered' },
  { key: 'completed', label: 'Complete'  },
];

const getStepIndex = (status) => {
  const map = {
    proposed: 0,
    accepted: 1,
    pickup_pending: 1,
    in_transit: 2,
    inspection: 2,
    in_progress: 2,
    delivery_pending: 3,
    completed: 4,
    rejected: -1,
    cancelled: -1,
  };
  return map[status] ?? 0;
};

const StatusStepper = ({ status }) => {
  const currentStep = getStepIndex(status);
  const isFailed = status === 'rejected' || status === 'cancelled';

  if (isFailed) {
    return (
      <div className="sd-stepper sd-stepper--failed">
        <FontAwesomeIcon icon={faTimesCircle} />
        <span>{status === 'rejected' ? 'Rejected' : 'Cancelled'}</span>
      </div>
    );
  }

  return (
    <div className="sd-stepper">
      {STEPPER_STEPS.map((step, idx) => (
        <div key={step.key} className={`sd-stepper__step ${idx <= currentStep ? 'active' : ''} ${idx === currentStep ? 'current' : ''}`}>
          <div className="sd-stepper__dot">
            {idx < currentStep && <FontAwesomeIcon icon={faCheckCircle} />}
            {idx === currentStep && <motion.div className="sd-stepper__pulse" animate={{ scale: [1, 1.4, 1] }} transition={{ repeat: Infinity, duration: 1.5 }} />}
          </div>
          <span className="sd-stepper__label">{step.label}</span>
          {idx < STEPPER_STEPS.length - 1 && (
            <div className="sd-stepper__line">
              <motion.div
                className="sd-stepper__line-fill"
                initial={{ width: '0%' }}
                animate={{ width: idx < currentStep ? '100%' : '0%' }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Versus card for sent/received deals
const VersusCard = ({ deal, type, onAccept, onReject, onCancel, onShip, onDeliver, onComplete, actionLoading }) => {
  const dealId = deal._id || deal.id;
  const isLoading = actionLoading === dealId;
  const status = deal.status || 'proposed';
  const isFailed = status === 'rejected' || status === 'cancelled';

  // deal is a SwapOffer (not the legacy initiator_product/recipient_product
  // shape this card used to expect): the initiator's own device lives on the
  // offer itself (flat `images` array), while the product being asked for is
  // `listing_product` (full product doc, images as [{url}] or [string]).
  const initiatorImg = deal.images?.[0] || null;
  const recipientImg = deal.listing_product?.images?.[0]?.url || deal.listing_product?.images?.[0] || deal.listing_product?.image || null;
  const initiatorName = [deal.device_type, deal.model, deal.storage].filter(Boolean).join(' ') || 'Their Item';
  const recipientName = deal.listing_product?.title || deal.listing_product?.name || 'Your Item';

  return (
    <motion.div
      className={`sd-versus-card ${isFailed ? 'sd-versus-card--failed' : ''}`}
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
    >
      {/* Top: Deal number + date */}
      <div className="sd-versus-card__meta">
        <span className="sd-versus-card__number">#{deal.swap_number || dealId?.slice(-8)?.toUpperCase()}</span>
        <span className="sd-versus-card__date">
          <FontAwesomeIcon icon={faClock} />
          {deal.created_at ? new Date(deal.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
        </span>
      </div>

      {/* Versus Layout */}
      <div className="sd-versus-layout">
        {/* Initiator side */}
        <div className="sd-versus-side">
          <div className="sd-versus-side__img-wrap">
            {initiatorImg
              ? <img src={initiatorImg} alt={initiatorName} />
              : <PlaceholderProduct label={type === 'sent' ? 'Your Item' : 'Their Item'} />}
          </div>
          <p className="sd-versus-side__name">{type === 'sent' ? 'You' : 'Initiator'}</p>
          <p className="sd-versus-side__product">{initiatorName}</p>
        </div>

        {/* Center connector */}
        <div className="sd-versus-center">
          <SwapConnectorSVG active={!isFailed} />
          {deal.seller_topup_amount > 0 && (
            <div className="sd-cash-chip">
              <FontAwesomeIcon icon={faTag} />
              ₦{Number(deal.seller_topup_amount).toLocaleString()}
            </div>
          )}
        </div>

        {/* Recipient side */}
        <div className="sd-versus-side">
          <div className="sd-versus-side__img-wrap">
            {recipientImg
              ? <img src={recipientImg} alt={recipientName} />
              : <PlaceholderProduct label={type === 'received' ? 'Your Item' : 'Their Item'} />}
          </div>
          <p className="sd-versus-side__name">{type === 'received' ? 'You' : 'Recipient'}</p>
          <p className="sd-versus-side__product">{recipientName}</p>
        </div>
      </div>

      {/* Bento micro-cards */}
      <div className="sd-bento-row">
        {deal.exchange_method && (
          <div className="sd-bento-chip">
            <FontAwesomeIcon icon={faTruck} />
            <span>{deal.exchange_method === 'shipping' ? 'Shipping' : 'Meetup'}</span>
          </div>
        )}
        {deal.meetup_location && (
          <div className="sd-bento-chip sd-bento-chip--location">
            <span>📍 {deal.meetup_location}</span>
          </div>
        )}
        {deal.balance_payer && (
          <div className="sd-bento-chip sd-bento-chip--payer">
            <span>{deal.balance_payer} pays balance</span>
          </div>
        )}
      </div>

      {/* Status stepper */}
      <StatusStepper status={status} />

      {/* Terms */}
      {deal.terms && (
        <div className="sd-terms">&ldquo;{deal.terms}&rdquo;</div>
      )}

      {/* Actions */}
      <div className="sd-versus-actions">
        {type === 'received' && status === 'proposed' && (
          <>
            <motion.button whileTap={{ scale: 0.96 }} className="sd-action-btn sd-action-btn--accept" onClick={() => onAccept(dealId)} disabled={isLoading}>
              {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheckCircle} />}
              Accept
            </motion.button>
            <motion.button whileTap={{ scale: 0.96 }} className="sd-action-btn sd-action-btn--reject" onClick={() => onReject(dealId)} disabled={isLoading}>
              <FontAwesomeIcon icon={faTimesCircle} /> Reject
            </motion.button>
          </>
        )}
        {type === 'sent' && status === 'proposed' && (
          <motion.button whileTap={{ scale: 0.96 }} className="sd-action-btn sd-action-btn--cancel" onClick={() => onCancel(dealId)} disabled={isLoading}>
            {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faBan} />}
            Cancel Offer
          </motion.button>
        )}
        {['accepted', 'pickup_pending'].includes(status) && (
          <motion.button whileTap={{ scale: 0.96 }} className="sd-action-btn sd-action-btn--ship" onClick={() => onShip(dealId)} disabled={isLoading}>
            {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTruck} />}
            Ship My Item
          </motion.button>
        )}
        {['in_transit', 'inspection', 'in_progress', 'delivery_pending'].includes(status) && (
          <motion.button whileTap={{ scale: 0.96 }} className="sd-action-btn sd-action-btn--accept" onClick={() => onDeliver(dealId, type === 'sent')} disabled={isLoading}>
            {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheckCircle} />}
            I Received It
          </motion.button>
        )}
        {status === 'delivery' && (
          <motion.button whileTap={{ scale: 0.96 }} className="sd-action-btn sd-action-btn--ship" onClick={() => onComplete(dealId)} disabled={isLoading}>
            {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheckCircle} />}
            Complete Swap
          </motion.button>
        )}
        {!['proposed', 'rejected', 'cancelled', 'completed'].includes(status) && (
          <motion.button whileTap={{ scale: 0.96 }} className="sd-action-btn sd-action-btn--ghost" onClick={() => onCancel(dealId)} disabled={isLoading}>
            <FontAwesomeIcon icon={faBan} /> Cancel
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

// Animated SVG connector for versus layout
const SwapConnectorSVG = ({ active }) => (
  <svg className={`sd-connector-svg ${active ? 'active' : 'inactive'}`} viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="30" cy="30" r="28" stroke="currentColor" strokeWidth="2" strokeDasharray="4 3" opacity="0.3" />
    <circle cx="30" cy="30" r="20" fill="currentColor" opacity="0.1" />
    <path d="M20 26l5-5 5 5M25 21v14M40 34l-5 5-5-5M35 39V25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Placeholder product illustration
const PlaceholderProduct = ({ label }) => (
  <div className="sd-placeholder-product">
    <svg viewBox="0 0 80 80" fill="none">
      <rect width="80" height="80" rx="12" fill="currentColor" opacity="0.07" />
      <rect x="20" y="18" width="40" height="30" rx="4" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
      <circle cx="30" cy="28" r="4" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
      <path d="M20 38l12-8 8 6 8-4 12 6" stroke="currentColor" strokeWidth="1.5" opacity="0.3" strokeLinejoin="round" />
      <rect x="24" y="54" width="32" height="4" rx="2" fill="currentColor" opacity="0.2" />
      <rect x="28" y="62" width="24" height="3" rx="1.5" fill="currentColor" opacity="0.12" />
    </svg>
    <span>{label}</span>
  </div>
);

// Card for buyer's sent swap offers (new POST /swap/offer format)
const OFFER_STATUS = {
  pending_seller_review:    { text: 'Awaiting Seller',  color: '#22C55E' },
  declined_by_seller:       { text: 'Seller Declined',  color: '#ef4444' },
  pending_initiator_review: { text: 'Review Offer',     color: '#6b7280' },
  declined_by_initiator:    { text: 'You Declined',     color: '#ef4444' },
  accepted_pending_payment: { text: 'Pay Now',          color: '#22C55E' },
  paid_in_escrow:           { text: 'In Escrow',       color: '#16A34A' },
  completed:                { text: 'Completed',        color: '#22c55e' },
  cancelled:                { text: 'Cancelled',        color: '#6b7280' },
};

const SentOfferCard = ({ offer, onAcceptPrice, onDeclinePrice, actionLoading }) => {
  const offerId = offer._id || offer.id;
  const statusInfo = OFFER_STATUS[offer.status] || { text: offer.status || 'Pending', color: '#6b7280' };
  const isLoading = actionLoading === offerId;
  const listingProduct = offer.listing_product || offer.product || null;
  const isSellerOffer = offer.status === 'pending_initiator_review';

  return (
    <motion.div
      className="sd-offer-card"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      layout
    >
      {/* Header */}
      <div className="sd-offer-card__header">
        <div>
          <span className="sd-offer-card__number">
            #{offer.swap_number || String(offerId).slice(-8).toUpperCase()}
          </span>
          <h4 className="sd-offer-card__model">
            {offer.model || 'Device'}{offer.storage ? ` · ${offer.storage}` : ''}
          </h4>
          <span className="sd-offer-card__specs">
            <FontAwesomeIcon icon={faMobileAlt} style={{ marginRight: 4 }} />
            {offer.device_type} · Condition {offer.condition_rating}/10
            {offer.battery_health != null ? ` · Battery ${offer.battery_health}%` : ''}
          </span>
        </div>
        <div className="sd-offer-card__badge-col">
          <span className="sd-offer-card__badge" style={{ background: statusInfo.color + '22', color: statusInfo.color }}>
            {statusInfo.text}
          </span>
          <span className="sd-offer-card__date">
            <FontAwesomeIcon icon={faClock} style={{ marginRight: 3 }} />
            {new Date(offer.created_at || offer.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        </div>
      </div>

      {/* What buyer wants to get */}
      {listingProduct && (
        <div className="sd-offer-card__target">
          {(listingProduct.images?.[0]?.url || listingProduct.images?.[0] || listingProduct.image) && (
            <img
              src={listingProduct.images?.[0]?.url || listingProduct.images?.[0] || listingProduct.image}
              alt={listingProduct.title || listingProduct.name}
              className="sd-offer-card__target-img"
              onError={e => { e.target.style.display = 'none'; }}
            />
          )}
          <div>
            <div className="sd-offer-card__target-label">Swapping for</div>
            <div className="sd-offer-card__target-name">{listingProduct.title || listingProduct.name || 'Listed product'}</div>
            {listingProduct.price && (
              <div className="sd-offer-card__target-price">₦{Number(listingProduct.price).toLocaleString()}</div>
            )}
          </div>
        </div>
      )}

      {/* Your device's photos */}
      {offer.images?.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', margin: '10px 0', flexWrap: 'wrap' }}>
          {offer.images.slice(0, 5).map((img, i) => (
            <img key={i} src={img} alt={`Your device ${i + 1}`}
              style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: '1px solid rgba(148,163,184,0.3)' }}
              onError={e => { e.target.style.display = 'none'; }}
            />
          ))}
          {offer.images.length > 5 && (
            <div style={{ width: '48px', height: '48px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(148,163,184,0.15)', fontSize: '12px', fontWeight: 700 }}>+{offer.images.length - 5}</div>
          )}
        </div>
      )}

      {/* Seller's counter top-up offer */}
      {isSellerOffer && offer.seller_topup_amount != null && (
        <div className="sd-offer-card__topup">
          <p className="sd-offer-card__topup-label">Seller requests top-up:</p>
          <p className="sd-offer-card__topup-amount">₦{Number(offer.seller_topup_amount).toLocaleString()}</p>
          <div className="sd-offer-card__topup-actions">
            <button
              className="sd-action-btn sd-action-btn--accept"
              onClick={() => onAcceptPrice(offerId)}
              disabled={isLoading}
            >
              {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheckCircle} />}
              Accept & Pay
            </button>
            <button
              className="sd-action-btn sd-action-btn--reject"
              onClick={() => onDeclinePrice(offerId)}
              disabled={isLoading}
            >
              <FontAwesomeIcon icon={faTimesCircle} /> Decline
            </button>
          </div>
        </div>
      )}

      {/* Status hint */}
      <p className="sd-offer-card__hint">
        {offer.status === 'pending_seller_review' && '⏳ Waiting for the seller to review your offer...'}
        {offer.status === 'declined_by_seller' && '❌ Seller declined. Try another product or submit a new offer.'}
        {isSellerOffer && !offer.seller_topup_amount && '📢 Seller responded — review the offer details above.'}
        {offer.status === 'accepted_pending_payment' && '💳 Accepted — proceed to payment to complete the swap.'}
        {offer.status === 'paid_in_escrow' && '🔒 Payment in escrow — swap is in progress.'}
        {offer.status === 'completed' && '✅ Swap completed successfully!'}
        {offer.status === 'cancelled' && '🚫 This offer was cancelled.'}
        {offer.status === 'declined_by_initiator' && '✗ You declined the seller\'s price.'}
      </p>

    </motion.div>
  );
};

// Available product card
const AvailableCard = ({ product, onOffer }) => {
  const navigate = useNavigate();

  // Handle multiple image formats: string, object with url, or nested structure
  const getImageUrl = () => {
    const firstImage = product.images?.[0];
    if (typeof firstImage === 'string') {
      return firstImage;
    } else if (firstImage?.url) {
      return firstImage.url;
    }
    return product.image || null;
  };

  const image = getImageUrl();
  const name = product.title || product.name || 'Product';
  const productId = product._id || product.id;

  return (
    <motion.div
      className="sd-available-card"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      onClick={() => productId && navigate(`/product/${productId}`)}
      style={{ cursor: 'pointer' }}
    >
      <div className="sd-available-card__img-wrap">
        {image
          ? <img src={image} alt={name} />
          : <PlaceholderProduct label={name} />}
        <div className="sd-available-card__badge">
          <FontAwesomeIcon icon={faExchangeAlt} /> Swap
        </div>
      </div>
      <div className="sd-available-card__body">
        <h3 className="sd-available-card__name">{name}</h3>
        <div className="sd-available-card__meta-row">
          <span className="sd-price-chip">₦{Number(product.price || 0).toLocaleString()}</span>
          {product.condition && (
            <span className="sd-condition-chip">{product.condition.replace(/_/g, ' ')}</span>
          )}
        </div>
        <motion.button
          className="sd-available-card__cta"
          onClick={(e) => { e.stopPropagation(); onOffer(product); }}
          whileTap={{ scale: 0.96 }}
        >
          <FontAwesomeIcon icon={faExchangeAlt} /> Make Offer
        </motion.button>
      </div>
    </motion.div>
  );
};

// === MAIN COMPONENT ===
const SwapDeals = ({ embedded = false, isDarkMode: propDarkMode }) => {
  const { showCustomToast } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const { getInitiatedSwaps, acceptPrice, declinePrice, fetchSwaps } = useSwap();

  const [activeTab, setActiveTab] = useState('available');
  const [availableDeals, setAvailableDeals] = useState([]);
  const [sentOffers, setSentOffers] = useState([]);
  const [receivedOffers, setReceivedOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // Read dark mode from global app state (body class or localStorage)
  const [darkMode, setDarkMode] = useState(() => {
    return document.body.classList.contains('dark-mode') ||
      localStorage.getItem('theme') === 'dark' ||
      window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Offer modal
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [selectedRecipientProduct, setSelectedRecipientProduct] = useState(null);

  const initiatedOffers = getInitiatedSwaps();

  const tabs = [
    { id: 'available', label: 'Available', count: availableDeals.length },
    { id: 'sent',      label: 'Sent',      count: initiatedOffers.length },
    { id: 'received',  label: 'Received',  count: receivedOffers.length },
  ];

  // Sync with global app dark mode (listen for changes)
  useEffect(() => {
    const handleStorageChange = () => {
      const isDark = document.body.classList.contains('dark-mode') ||
                     localStorage.getItem('theme') === 'dark';
      setDarkMode(isDark);
    };

    window.addEventListener('storage', handleStorageChange);
    // Also watch for class changes on body
    const observer = new MutationObserver(handleStorageChange);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    fetchSwapDeals();
    if (location.state?.selectedProduct && location.state?.action === 'create_offer') {
      openOfferModal(location.state.selectedProduct);
    }
  }, []);

  const fetchSwapDeals = async () => {
    try {
      setLoading(true);
      // Fetch swap-available products (same as Homepage Swap Zone)
      const productsRes = await productsAPI.getAll({
        limit: 50,
        swap_available: true
      });
      const allProducts = productsRes?.data?.products || productsRes?.data || [];
      const swapProducts = Array.isArray(allProducts) ? allProducts : [];
      setAvailableDeals(swapProducts);

      const currentUserId = getCurrentUserId();
      if (currentUserId) {
        const swapRes = await swapAPI.getSwaps();
        // GET /swap/offers → { data: { swap_offers: [...] } }
        const allDeals = swapRes?.data?.data?.swap_offers
          || swapRes?.data?.data
          || swapRes?.data?.swap_offers
          || swapRes?.data?.swap_deals
          || swapRes?.data
          || [];
        if (Array.isArray(allDeals)) {
          setSentOffers(allDeals.filter(d =>
            String(d.initiator_id) === String(currentUserId) || d.initiator_type === 'buyer'
          ));
          setReceivedOffers(allDeals.filter(d =>
            String(d.listing_seller_id) === String(currentUserId) ||
            (String(d.recipient_id) === String(currentUserId) && d.initiator_type !== 'buyer')
          ));
        }
      }
    } catch (error) {
      if (error?.response?.status !== 403) {
        console.error('Swap fetch error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handlers for sent offers (new swap offer format via SwapContext)
  const handleAcceptPrice = async (swapId) => {
    if (!window.confirm('Accept this seller price offer?')) return;
    setActionLoading(swapId);
    try { await acceptPrice(swapId); }
    catch { /* toast shown by SwapContext */ }
    finally { setActionLoading(null); }
  };

  const handleDeclinePrice = async (swapId) => {
    if (!window.confirm('Decline this price offer?')) return;
    setActionLoading(swapId);
    try { await declinePrice(swapId); }
    catch { /* toast shown by SwapContext */ }
    finally { setActionLoading(null); }
  };

  const openOfferModal = (product) => {
    setSelectedRecipientProduct(product);
    setShowOfferModal(true);
  };

  const closeOfferModal = () => {
    setShowOfferModal(false);
    setSelectedRecipientProduct(null);
  };


  const handleAcceptOffer = async (swapId) => {
    if (!window.confirm('Accept this swap offer?')) return;
    try {
      setActionLoading(swapId);
      await swapAPI.acceptSwapDeal(swapId);
      showCustomToast({ type: 'success', message: 'Swap offer accepted! Please arrange shipment with the other party.' });
      fetchSwapDeals();
    } catch (error) {
      showCustomToast({ type: 'error', message: error?.response?.data?.error || error.message || 'Failed to accept offer' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectOffer = async (swapId) => {
    const reason = window.prompt('Reason for rejecting (optional):');
    if (reason === null) return;
    try {
      setActionLoading(swapId);
      await swapAPI.rejectSwapDeal(swapId, reason);
      fetchSwapDeals();
    } catch (error) {
      showCustomToast({ type: 'error', message: error?.response?.data?.error || error.message || 'Failed to reject offer' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelOffer = async (swapId) => {
    const reason = window.prompt('Reason for cancelling (optional):');
    if (reason === null) return;
    try {
      setActionLoading(swapId);
      await swapAPI.cancelSwapDeal(swapId, reason);
      fetchSwapDeals();
    } catch (error) {
      showCustomToast({ type: 'error', message: error?.response?.data?.error || error.message || 'Failed to cancel offer' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmShipment = async (swapId) => {
    const trackingNumber = window.prompt('Enter tracking number:');
    if (!trackingNumber) return;
    const carrierName = window.prompt('Enter carrier name (e.g. DHL, FedEx):') || '';
    try {
      setActionLoading(swapId);
      await swapAPI.confirmShipment(swapId, { tracking_number: trackingNumber, carrier_name: carrierName });
      showCustomToast({ type: 'success', message: 'Shipment confirmed!' });
      fetchSwapDeals();
    } catch (error) {
      showCustomToast({ type: 'error', message: error?.response?.data?.error || error.message || 'Failed to confirm shipment' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmDelivery = async (swapId, isInitiator) => {
    if (!window.confirm('Confirm that you have received the item from this swap?')) return;
    try {
      setActionLoading(swapId);
      await swapAPI.confirmDelivery(swapId, isInitiator);
      showCustomToast({ type: 'success', message: 'Delivery confirmed! Waiting for the other party to confirm.' });
      fetchSwapDeals();
    } catch (error) {
      showCustomToast({ type: 'error', message: error?.response?.data?.error || error.message || 'Failed to confirm delivery' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCompleteSwap = async (swapId) => {
    if (!window.confirm('Mark this swap deal as complete?')) return;
    try {
      setActionLoading(swapId);
      await swapAPI.completeSwapDeal(swapId);
      showCustomToast({ type: 'success', message: 'Swap deal completed successfully!' });
      fetchSwapDeals();
    } catch (error) {
      showCustomToast({ type: 'error', message: error?.response?.data?.error || error.message || 'Failed to complete swap' });
    } finally {
      setActionLoading(null);
    }
  };

  // ── Empty state illustrations ──
  const EmptyAvailable = () => (
    <div className="sd-empty">
      <svg className="sd-empty__svg" viewBox="0 0 200 160" fill="none">
        <rect x="20" y="30" width="70" height="90" rx="10" fill="currentColor" opacity="0.06" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.15" />
        <rect x="110" y="30" width="70" height="90" rx="10" fill="currentColor" opacity="0.06" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.15" />
        <path d="M90 75 Q100 65 110 75" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3" fill="none" strokeLinecap="round" />
        <path d="M90 85 Q100 95 110 85" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3" fill="none" strokeLinecap="round" />
        <circle cx="55" cy="60" r="14" fill="currentColor" opacity="0.1" />
        <circle cx="145" cy="60" r="14" fill="currentColor" opacity="0.1" />
        <rect x="35" y="82" width="40" height="6" rx="3" fill="currentColor" opacity="0.12" />
        <rect x="40" y="94" width="30" height="4" rx="2" fill="currentColor" opacity="0.08" />
        <rect x="125" y="82" width="40" height="6" rx="3" fill="currentColor" opacity="0.12" />
        <rect x="130" y="94" width="30" height="4" rx="2" fill="currentColor" opacity="0.08" />
        <motion.circle cx="100" cy="75" r="6" fill="#22c55e" opacity="0.6"
          animate={{ scale: [1, 1.3, 1], opacity: [0.6, 1, 0.6] }}
          transition={{ repeat: Infinity, duration: 2 }} />
      </svg>
      <h3>No Swap Products Listed</h3>
      <p>Sellers haven't made any items available for swap yet.</p>
    </div>
  );

  const EmptyDeals = ({ label }) => (
    <div className="sd-empty">
      <svg className="sd-empty__svg" viewBox="0 0 200 160" fill="none">
        <rect x="40" y="20" width="120" height="100" rx="12" fill="currentColor" opacity="0.05" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.12" strokeDasharray="6 3" />
        <motion.path d="M80 70 L100 50 L120 70 M100 50 V90" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.25"
          animate={{ pathLength: [0, 1, 0] }} transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }} />
        <rect x="60" y="100" width="80" height="8" rx="4" fill="currentColor" opacity="0.1" />
        <rect x="70" y="114" width="60" height="6" rx="3" fill="currentColor" opacity="0.07" />
      </svg>
      <h3>No {label}</h3>
      <p>{label === 'Sent Offers' ? 'Browse available products and make your first swap.' : "You haven't received any swap offers yet."}</p>
      {label === 'Sent Offers' && (
        <button className="sd-empty__cta" onClick={() => setActiveTab('available')}>
          <FontAwesomeIcon icon={faExchangeAlt} /> Browse Products
        </button>
      )}
    </div>
  );

  // When embedded, respect the parent dashboard's dark mode if provided
  const resolvedDark = propDarkMode !== undefined ? propDarkMode : darkMode;

  if (loading) {
    return (
      <div className={embedded ? '' : `sd-root ${resolvedDark ? 'sd-dark' : ''}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: embedded ? '200px' : '100vh' }}>
        <motion.div className="sd-loader" animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}>
          <div className="sd-loader__ring" />
        </motion.div>
      </div>
    );
  }

  // Shared content (tabs + deals + modal) used in both embedded and standalone modes
  const sharedContent = (
    <>
      {/* Glass pill tabs */}
      <div className="sd-tabs-wrap">
        <div className="sd-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`sd-tab ${activeTab === tab.id ? 'sd-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {activeTab === tab.id && (
                <motion.div
                  className="sd-tab__bg"
                  layoutId="sd-tab-indicator"
                  transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                />
              )}
              <span className="sd-tab__label">{tab.label}</span>
              <span className="sd-tab__count">{tab.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="sd-main">
        <AnimatePresence mode="wait">
          {activeTab === 'available' && (
            <motion.div key="available" className="sd-available-grid"
              initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }} transition={{ duration: 0.25 }}>
              {availableDeals.length === 0
                ? <EmptyAvailable />
                : availableDeals.map((p, i) => (
                  <motion.div key={p._id || p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                    <AvailableCard product={p} onOffer={openOfferModal} />
                  </motion.div>
                ))}
            </motion.div>
          )}

          {activeTab === 'sent' && (
            <motion.div key="sent" className="sd-deals-list"
              initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }} transition={{ duration: 0.25 }}>
              {initiatedOffers.length === 0
                ? <EmptyDeals label="Sent Offers" />
                : initiatedOffers.map(d => (
                  <SentOfferCard
                    key={d._id || d.id}
                    offer={d}
                    onAcceptPrice={handleAcceptPrice}
                    onDeclinePrice={handleDeclinePrice}
                    actionLoading={actionLoading}
                  />
                ))}
            </motion.div>
          )}

          {activeTab === 'received' && (
            <motion.div key="received" className="sd-deals-list"
              initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.25 }}>
              {receivedOffers.length === 0
                ? <EmptyDeals label="Received Offers" />
                : receivedOffers.map(d => (
                  <VersusCard key={d._id || d.id} deal={d} type="received"
                    onAccept={handleAcceptOffer} onReject={handleRejectOffer}
                    onCancel={handleCancelOffer} onShip={handleConfirmShipment}
                    onDeliver={handleConfirmDelivery} onComplete={handleCompleteSwap}
                    actionLoading={actionLoading} />
                ))}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Make Offer Modal */}
      <AnimatePresence>
        {showOfferModal && selectedRecipientProduct && (
          <motion.div
            className="sd-modal-overlay"
            onClick={closeOfferModal}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <motion.div
              className="sd-modal"
              onClick={e => e.stopPropagation()}
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 320, damping: 26, mass: 0.8 }}
            >
              <SwapOfferForm
                productId={selectedRecipientProduct._id || selectedRecipientProduct.id}
                onSuccess={() => { closeOfferModal(); fetchSwaps(); fetchSwapDeals(); setActiveTab('sent'); }}
                onCancel={closeOfferModal}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  if (embedded) {
    return (
      <div className={`sd-embedded-wrap ${resolvedDark ? 'sd-dark' : ''}`}>
        {sharedContent}
      </div>
    );
  }

  return (
    <div className={`sd-root ${resolvedDark ? 'sd-dark' : ''}`}>
      <header className="sd-header">
        <button className="sd-back" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h1 className="sd-title">
          <FontAwesomeIcon icon={faExchangeAlt} /> Swap Deals
        </h1>
        <button className="sd-dark-toggle" onClick={() => {
          const newDark = !resolvedDark;
          setStoredDarkMode(newDark);
          document.body.classList.toggle('dark-mode', newDark);
          setDarkMode(newDark);
        }}>
          <FontAwesomeIcon icon={resolvedDark ? faSun : faMoon} />
        </button>
      </header>
      {sharedContent}
    </div>
  );
};

export default SwapDeals;
